// Shared XHR-based file upload used by both the manual PAD upload popup
// (PadUploadPopup.tsx) and the automated browser folder-sync feature — extracted
// out of PadUploadPopup.tsx so both call sites hit the exact same endpoint/headers/
// validation instead of two copies drifting apart. Kept as XMLHttpRequest (not the
// apiClient `apiRequest` helper) specifically to get upload-progress events, same
// reason the original inline version used it.
import { config } from "configs/config";
import { invalidateForMutation } from "src/services/apiCache";

export function xhrRequest(
    url: string,
    file: File,
    extraFields?: Record<string, string>,
    onProgress?: (pct: number) => void,
    onUploaded?: () => void,
): Promise<any> {
    return new Promise((resolve, reject) => {
        const token    = localStorage.getItem("token");
        const praxisId = JSON.parse(localStorage.getItem("praxis") || "null")?.id;
        const doctorId = JSON.parse(localStorage.getItem("doctor") || "null")?.id;

        const xhr = new XMLHttpRequest();
        xhr.open("POST", url);
        if (token)    xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        if (doctorId) xhr.setRequestHeader("X-Doctor-Id", doctorId);
        if (praxisId) xhr.setRequestHeader("X-Praxis-Id", praxisId);

        if (onProgress) {
            xhr.upload.addEventListener("progress", (e) => {
                if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
            });
        }
        xhr.upload.addEventListener("load", () => onUploaded?.());

        xhr.addEventListener("load", () => {
            if (xhr.status === 401) { reject(new Error("Unauthorized")); return; }
            let parsed: any;
            try { parsed = JSON.parse(xhr.responseText); }
            catch { reject(new Error("Invalid response")); return; }
            // Any non-2xx status is a failure even when the body has no `error` field —
            // e.g. requireRole.ts's permission middleware responds 403 with only `message`,
            // which this used to silently resolve as success (empty summary, no error shown).
            if (xhr.status >= 400) { reject(new Error(parsed.message || parsed.error || `Request failed (${xhr.status})`)); return; }
            // apiRequest() (fetch-based) triggers this automatically after every non-GET —
            // XHR bypasses that path entirely, so without this call a successful upload
            // left every open page's cached data (e.g. the Invoices list) stale until some
            // unrelated navigation happened to force a refetch. Strip config.baseUrl since
            // invalidateForMutation matches on the path, not the full absolute URL.
            const relativePath = url.startsWith(config.baseUrl) ? url.slice(config.baseUrl.length) : url;
            invalidateForMutation(relativePath);
            resolve(parsed);
        });
        xhr.addEventListener("error", () => reject(new Error("Network error")));

        const fd = new FormData();
        fd.append("file", file);
        if (extraFields) {
            for (const [k, v] of Object.entries(extraFields)) fd.append(k, v);
        }
        xhr.send(fd);
    });
}

/** Convenience wrapper around xhrRequest for the plain "upload a .dat/PAD file" call —
 *  used by the browser folder-sync poll loop. */
export function uploadPadFile(file: File, onProgress?: (pct: number) => void): Promise<any> {
    return xhrRequest(`${config.baseUrl}/api/upload`, file, undefined, onProgress);
}
